import PropTypes from 'prop-types';
import React from 'react';
import { merge, split, path, pathOr, prop, map, replace, defaultTo, mergeAll } from 'ramda';
import { cookie } from 'redux-effects-universal-cookie';
import { fetchPlayerInfo, setRealityCheckSettings, removeRealityCheckSettings } from '@rushplay/api-client';
import { bind } from 'redux-effects';
import { connect } from 'react-redux';
import { Timestamp } from '@rushplay/common';
import { add } from '@rushplay/notifications';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

/**
 * Timer mechanism:
 * - componentDidMount: Retrieve the timer data from cookies (a running interval).
 * Set the timer in the Redux store with retrieved data.
 * - componentDidUpdate:
 * 1. Check if current time has changed and change the time remaining until reality
 * check accordingly
 * 2. Check if time interval has expired; if interval has expired,
 * the local state interval, state and session cookie are reset to the inital interval.
 * - componentWillUnmount: Saves the time interval at, to state and session cookie.
 */

var Timer =
/*#__PURE__*/
function (_React$Component) {
  _inherits(Timer, _React$Component);

  function Timer() {
    var _this;

    _classCallCheck(this, Timer);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Timer).call(this));
    _this.handleUnload = _this.handleUnload.bind(_assertThisInitialized(_this));
    return _this;
  }

  _createClass(Timer, [{
    key: "handleUnload",
    value: function handleUnload() {
      // TODO: prevent componentDidUpdate calls after we call handleUnload
      this.props.onLeave(this.props.timeToRealityCheck);
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      this.props.onMount();
      window.addEventListener('beforeunload', this.handleUnload);
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      if (this.props.realityCheckEnabled) {
        if (prevProps.currentTime !== this.props.currentTime) {
          var delta = this.props.currentTime - prevProps.currentTime;
          this.props.onUpdate(this.props.timeToRealityCheck - delta);
        }

        if (this.props.timeToRealityCheck <= 0) {
          this.props.onTrigger();
        }
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.props.onLeave(this.props.timeToRealityCheck);
      window.removeEventListener('beforeunload', this.handleUnload);
    }
  }, {
    key: "render",
    value: function render() {
      if (this.props.children) {
        return React.cloneElement(this.props.children, {
          realityCheckTimeRemaining: this.props.timeToRealityCheck,
          currentTime: this.props.currentTime
        });
      }

      return null;
    }
  }]);

  return Timer;
}(React.Component);

Timer.propTypes = {
  children: PropTypes.node,
  currentTime: PropTypes.number,
  realityCheckEnabled: PropTypes.bool,
  timeToRealityCheck: PropTypes.number,
  onLeave: PropTypes.func,
  onMount: PropTypes.func,
  onTrigger: PropTypes.func,
  onUpdate: PropTypes.func
};

var PERIOD_UPDATED = 'compliance/reality-check/PERIOD_UPDATED';
var PERIOD_REMOVED = 'compliance/reality-check/PERIOD_REMOVED';
var TIME_UPDATED = 'compliance/reality-check/TIME_UPDATED';
var TIME_OVER = 'compliance/reality-check/TIME_OVER';
var ACKNOWLEDGED = 'compliance/reality-check/ACKNOWLEDGED'; // TODO: remove when notifications are a separate module

var SET_SUCCESSFULLY = 'compliance/reality-check/SET_SUCCESSFULLY';

/**
 * Resets reality check timer
 * @returns {Object} Redux action
 */

function resetRealityCheckTimer() {
  return {
    type: TIME_OVER
  };
}
/**
 * Updates time to reality check
 * @param {number} timeToRealityCheck Time left until next reality check in ms
 * @param {number} realityCheckPeriod Reality check period from the user's settings in ms
 * @param {string} currentSessionToken The session token from current session
 * @returns {Object} Redux action
 */

function updateTimeToRealityCheck(timeToRealityCheck, sessionToken) {
  return [{
    type: TIME_UPDATED,
    payload: timeToRealityCheck
  }, cookie('timeToRealityCheck', "".concat(timeToRealityCheck, ".").concat(sessionToken), {
    path: '/'
  })];
}
/**
 * Set reality check period
 * @param {number} period Reality check period in minutes
 * @returns {Object} Redux action
 */

function setRealityCheckPeriod(period) {
  return {
    type: PERIOD_UPDATED,
    payload: period,
    error: period instanceof Error
  };
}
/**
 * Remove reality check period
 * @param {Object} period is only supplied if error was returned on API request
 * @returns {Object} Redux action
 */

function removeRealityCheckPeriod(error) {
  return {
    type: PERIOD_REMOVED,
    payload: error,
    error: error instanceof Error
  };
}
/**
 * Marks reality check as acknowledged
 * @returns {Object} Redux action
 */

function acknowledgeRealityCheck() {
  return {
    type: ACKNOWLEDGED
  };
}
/**
 * Fetches the reality check data from BE
 * @returns {Object} Redux action
 */

function fetch() {
  return fetchPlayerInfo({
    success: function success(res) {
      return setRealityCheckPeriod(path(['value', 'result', 'realityCheckPeriod'], res));
    },
    failure: function failure() {
      return setRealityCheckPeriod(new Error('player-info.errors.failed-loading'));
    },
    version: 2
  });
} // TODO: remove when notifications are a separate module

/**
 * Shows reality check setting successful notification
 * @returns {Object} Redux action
 */

function showRealityCheckSetNotification() {
  return {
    type: SET_SUCCESSFULLY
  };
}

/**
 * Gets pending reality check
 * @param {Object} state Reality check state
 * @returns {boolean}
 */

function getPendingRealityCheck(state) {
  return pathOr(false, ['pendingRealityCheck'], state);
}
/**
 * Gets time to reality check
 * @param {Object} state Reality check state
 * @returns {number} Reality check period
 */

function getTimeToRealityCheck(state) {
  return path(['timeToRealityCheck'], state);
}
/**
 * Gets reality check period
 * @param {Object} state Reality check state
 * @returns {number} Reality check period
 */

function getRealityCheckPeriod(state) {
  return pathOr(null, ['realityCheckPeriod'], state);
}

function splitCookie(cookie$$1, currentSessionToken) {
  if (!cookie$$1) {
    return null;
  }

  var _R$split = split('.', cookie$$1),
      _R$split2 = _slicedToArray(_R$split, 2),
      time = _R$split2[0],
      token = _R$split2[1];

  return token === currentSessionToken ? parseInt(time) : null;
}
/**
 * Updates store and session cookie
 * @param {number} timeToRealityCheckFromStore Time interval remaining from the Redux store in ms
 * @param {number} realityCheckPeriodFromStore The reality check period setting from the Redux store in ms
 * @param {string} currentSessionToken The session token from current session
 * @returns {Object} Redux action
 */


function restoreRealityCheck(currentSessionToken) {
  return bind(cookie('timeToRealityCheck'), function (timeToRealityCheckFromCookie) {
    var timeToRealityCheck = splitCookie(timeToRealityCheckFromCookie, currentSessionToken);

    if (timeToRealityCheck) {
      return updateTimeToRealityCheck(timeToRealityCheck, currentSessionToken);
    }
  });
}

function mapStateToProps(state, props) {
  var localState = prop(props.mountPoint, state);
  return {
    realityCheckEnabled: Boolean(getRealityCheckPeriod(localState)),
    realityCheckPeriod: getRealityCheckPeriod(localState),
    timeToRealityCheck: getTimeToRealityCheck(localState) <= 0 ? getTimeToRealityCheck(localState) : getTimeToRealityCheck(localState) || getRealityCheckPeriod(localState)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onUpdate: function onUpdate(intervalRemaining, sessionToken) {
      return dispatch(updateTimeToRealityCheck(intervalRemaining, sessionToken));
    },
    onTrigger: function onTrigger() {
      return dispatch(resetRealityCheckTimer());
    },
    onMount: function onMount(sessionToken) {
      return dispatch(restoreRealityCheck(sessionToken));
    }
  };
}

function mergeProps(stateProps, dispatchProps, ownProps) {
  return mergeAll([ownProps, stateProps, dispatchProps, {
    onUpdate: function onUpdate(intervalRemaining) {
      return dispatchProps.onUpdate(intervalRemaining, ownProps.sessionToken);
    },
    onMount: function onMount() {
      return dispatchProps.onMount(ownProps.sessionToken);
    },
    onLeave: function onLeave(intervalRemaining) {
      return ownProps.timerResetTrigger === 'session-end' ? dispatchProps.onUpdate(intervalRemaining, ownProps.sessionToken) : dispatchProps.onUpdate(stateProps.realityCheckPeriod, ownProps.sessionToken);
    }
  }]);
}

var connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);

function Component(props) {
  if (!props.realityCheckPeriod) {
    return props.children;
  }

  return React.createElement(Timestamp, null, function (timestamp) {
    return React.createElement(Timer, _extends({}, props, {
      currentTime: timestamp
    }));
  });
}

var RealityCheckTimer = connector(Component);

/**
 * A function to be called when reality check is open.
 * @name onOpen
 * @function
 * @param {function} callback Redux action
 * @returns {Object} Redux action
 */

var Popup =
/*#__PURE__*/
function (_React$PureComponent) {
  _inherits(Popup, _React$PureComponent);

  function Popup() {
    _classCallCheck(this, Popup);

    return _possibleConstructorReturn(this, _getPrototypeOf(Popup).apply(this, arguments));
  }

  _createClass(Popup, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      window.addEventListener('beforeunload', this.props.onAcknowledge);

      if (this.props.onOpen) {
        this.props.onOpen();
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      if (this.props.onClose) {
        this.props.onClose();
      }

      this.props.onAcknowledge();
      window.removeEventListener('beforeunload', this.props.onAcknowledge);
    }
  }, {
    key: "getStateAndHelpers",
    value: function getStateAndHelpers() {
      return {
        period: this.props.period,
        isOpen: this.props.isOpen,
        onAcknowledge: this.props.onAcknowledge
      };
    }
  }, {
    key: "render",
    value: function render() {
      return this.props.children(this.getStateAndHelpers());
    }
  }]);

  return Popup;
}(React.PureComponent);

Popup.defaultProps = {
  isOpen: false
};
Popup.propTypes = {
  children: PropTypes.func,
  period: PropTypes.number,
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onAcknowledge: PropTypes.func
};

function mapStateToProps$1(state, props) {
  var realityCheckState = prop(props.mountPoint, state);
  return {
    period: getRealityCheckPeriod(realityCheckState),
    isOpen: getPendingRealityCheck(realityCheckState)
  };
}

function mapDispatchToProps$1(dispatch) {
  return {
    onAcknowledge: function onAcknowledge(period, sessionToken) {
      return dispatch([acknowledgeRealityCheck(), updateTimeToRealityCheck(period, sessionToken)]);
    }
  };
}

function mergeProps$1(stateProps, dispatchProps, ownProps) {
  return mergeAll([ownProps, stateProps, dispatchProps, {
    onAcknowledge: function onAcknowledge() {
      return dispatchProps.onAcknowledge(stateProps.period, ownProps.sessionToken);
    }
  }]);
}

var connector$1 = connect(mapStateToProps$1, mapDispatchToProps$1, mergeProps$1);

var RealityCheckPopup = connector$1(Popup);

/**
 * A function to be called when player failed
 * to submit a reality check period
 * @name onError
 * @function
 * @param {function} callback Redux action
 * @returns {Object} Redux action
 */

/**
 * Submits reality check period
 * @name onSet
 * @function
 * @param {String} period Period
 * @returns {Object} Redux action
 */

/**
 * Removes reality check period
 * @name onRemove
 * @function
 * @param {String} period Period
 * @returns {Object} Redux action
 */

function Settings(props) {
  return props.children({
    period: props.period,
    options: props.options,
    onError: props.onError,
    onSet: props.onSet,
    onRemove: props.onRemove
  });
}

Settings.propTypes = {
  children: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    labelKey: PropTypes.string,
    value: PropTypes.string
  })),
  period: PropTypes.string,
  onError: PropTypes.func,
  onSet: PropTypes.func,
  onRemove: PropTypes.func
};

function mapStateToProps$2(state, props) {
  var realityCheckState = prop(props.mountPoint, state);
  return {
    period: getRealityCheckPeriod(realityCheckState),
    options: map(function (item) {
      return {
        value: item,
        labelKey: "reality-check.options.".concat(replace(' ', '-', item))
      };
    }, defaultTo([], props.values))
  };
}

function mapDispatchToProps$2(dispatch) {
  return {
    onSet: function onSet(period, sessionToken) {
      dispatch(setRealityCheckSettings(period, {
        success: function success(res) {
          var period = pathOr(null, ['value', 'result', 'period'], res);
          return [setRealityCheckPeriod(period), updateTimeToRealityCheck(period, sessionToken), // TODO: remove when we refactor/get rid of SimpleCasino
          showRealityCheckSetNotification(), add({
            message: 'reality-check.update.success',
            level: 'success'
          })];
        },
        failure: function failure() {
          return setRealityCheckPeriod(new Error('reality-check.settings-failed'));
        },
        version: 2
      }));
    },
    onRemove: function onRemove(sessionToken) {
      dispatch(removeRealityCheckSettings({
        success: function success() {
          return [removeRealityCheckPeriod(), updateTimeToRealityCheck(null, sessionToken), // TODO: remove when we refactor/get rid of SimpleCasino
          showRealityCheckSetNotification(), add({
            message: 'reality-check.update.success',
            level: 'success'
          })];
        },
        failure: function failure() {
          return removeRealityCheckPeriod(new Error('reality-check.settings-failed'));
        },
        version: 2
      }));
    }
  };
}

function mergeProps$2(stateProps, dispatchProps, ownProps) {
  return mergeAll([stateProps, dispatchProps, ownProps, {
    onSet: function onSet(period) {
      return dispatchProps.onSet(period, ownProps.sessionToken);
    },
    onRemove: function onRemove() {
      return dispatchProps.onRemove(ownProps.sessionToken);
    }
  }]);
}

var connector$2 = connect(mapStateToProps$2, mapDispatchToProps$2, mergeProps$2);
var RealityCheckSettings = connector$2(Settings);

function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;

  if (action.error) {
    return state;
  }

  switch (action.type) {
    case ACKNOWLEDGED:
      {
        return merge(state, {
          pendingRealityCheck: false,
          timeToRealityCheck: state.realityCheckPeriod
        });
      }

    case PERIOD_UPDATED:
      {
        return merge(state, {
          realityCheckPeriod: parseInt(action.payload)
        });
      }

    case PERIOD_REMOVED:
      {
        return merge(state, {
          realityCheckPeriod: null
        });
      }

    case TIME_UPDATED:
      {
        return merge(state, {
          timeToRealityCheck: action.payload
        });
      }

    case TIME_OVER:
      {
        return merge(state, {
          pendingRealityCheck: true,
          timeToRealityCheck: Number.MAX_SAFE_INTEGER
        });
      }

    default:
      {
        return state;
      }
  }
}

export { RealityCheckTimer, RealityCheckPopup, connector$2 as connector, RealityCheckSettings, restoreRealityCheck, reducer, resetRealityCheckTimer, updateTimeToRealityCheck, setRealityCheckPeriod, removeRealityCheckPeriod, acknowledgeRealityCheck, fetch, showRealityCheckSetNotification, getPendingRealityCheck, getTimeToRealityCheck, getRealityCheckPeriod, PERIOD_UPDATED, PERIOD_REMOVED, TIME_UPDATED, TIME_OVER, ACKNOWLEDGED, SET_SUCCESSFULLY };
